import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTable } from '@tanstack/react-table';
import type { FilterFn } from '@tanstack/table-core';
import { useState } from 'react';

import {
  DataTable,
  type DataTableFeatures,
  DataTablePagination,
  dataTableColumnHelper,
  dataTableDefaultColumn,
  dataTableFeatures,
} from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { useAuth } from '../lib/auth.tsx';
import { listPages, type PageListItem } from '../lib/pages.ts';

export const Route = createFileRoute('/pages/')({
  component: PagesListPage,
});

const column = dataTableColumnHelper<PageListItem>();

/**
 * Title or slug, in one pass over the row.
 *
 * The slug is rendered under the title rather than in a column of its own, so
 * the stock per-column filter cannot see it -- and the slug is what somebody
 * types when they are looking for the page a URL points at.
 */
const matchesSearch: FilterFn<DataTableFeatures, PageListItem> = (row, _columnId, value) => {
  const term = String(value ?? '')
    .trim()
    .toLowerCase();

  if (!term) return true;

  return (
    row.original.title.toLowerCase().includes(term) ||
    row.original.slug.toLowerCase().includes(term)
  );
};

const columns = column.columns([
  column.accessor('title', {
    header: 'Title',
    cell: ({ row }) => (
      <div className="min-w-0">
        <Link
          to="/pages/$pageId"
          params={{ pageId: row.original.id }}
          className="block truncate font-medium hover:underline"
        >
          {row.original.title}
        </Link>
        <div className="text-muted-foreground truncate text-xs">/{row.original.slug}</div>
      </div>
    ),
  }),

  column.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => (
      <Badge variant={getValue() === 'published' ? 'default' : 'secondary'} className="capitalize">
        {getValue()}
      </Badge>
    ),
  }),

  column.accessor('updatedAt', {
    header: 'Last edited',
    cell: ({ getValue }) => {
      const value = getValue();

      return (
        <span className="text-muted-foreground text-sm">
          {value
            ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(value))
            : '—'}
        </span>
      );
    },
  }),

  column.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: 'text-right' },
    cell: ({ row }) => (
      <Button asChild variant="ghost" size="sm">
        <Link to="/pages/$pageId" params={{ pageId: row.original.id }}>
          Edit
        </Link>
      </Button>
    ),
  }),
]);

function PagesListPage() {
  const { activeAccountId } = useAuth();
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState('');

  /**
   * Plain TanStack Query rather than a TanStack DB collection: this list is
   * read once when the screen opens and there is nothing to gain from syncing
   * every page row into the client store.
   */
  const { data: pages, isLoading } = useQuery({
    queryKey: ['pages', activeAccountId],
    queryFn: () => listPages(activeAccountId as string),
    enabled: Boolean(activeAccountId),
  });

  const rows = pages ?? [];

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns,
    data: rows,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    initialState: { pagination: { pageIndex: 0, pageSize: 25 } },
    globalFilterFn: matchesSearch,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pages</h1>
          <p className="text-muted-foreground text-sm">
            {isLoading ? 'Loading…' : `${rows.length} page${rows.length === 1 ? '' : 's'}`}
          </p>
        </div>

        <Input
          placeholder="Filter by title or slug…"
          value={globalFilter}
          onChange={(event) => setGlobalFilter(event.target.value)}
          className="w-72"
        />
      </div>

      <DataTable
        table={table}
        columnCount={columns.length}
        isLoading={isLoading}
        onRowActivate={(row) => navigate({ to: '/pages/$pageId', params: { pageId: row.id } })}
        emptyMessage={globalFilter ? 'No pages match that filter.' : 'No pages yet.'}
      />

      <DataTablePagination table={table} total={rows.length} />
    </div>
  );
}
